import { Container, Box, Flex, Pagination } from '@legion-ui/core';
import RequestsTable from '@/components/RequestsTable';
import { usePublisherJourneyQuery } from '@/hooks/usePublisherJourneyQuery';
import { tableStore } from '@/store/table';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { setLocalStorage } from '@/services';
import Cookies from 'js-cookie';

const RequestPage = () => {
  // State untuk filter dan pencarian
  const [searchParam, setSearchParam] = useSearchParams();
  const [searchUsername, setSearchUsername] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const location = useLocation();
  const navigate = useNavigate();

  // Get pagination state from store
  const { currentPage, setCurrentPage, setPageSize, pageSize } =
    tableStore((state) => state);

  // Initialize dari URL params
  useEffect(() => {
    const initialUsername = searchParam.get('findUsers') || '';
    const initialStatus = searchParam.get('status') || '';
    const initialSort = (searchParam.get('sortOrder') as 'ASC' | 'DESC') || 'DESC';
    const initialPage = Number(searchParam.get('page')) || 1;
    const initialSize = Number(searchParam.get('size')) || 10;
    
    setSearchUsername(initialUsername);
    setStatusFilter(initialStatus);
    setSortOrder(initialSort);
    
    // Set pagination state dari URL
    setCurrentPage(initialPage);
    setPageSize(initialSize);
  }, [searchParam, setCurrentPage, setPageSize]);

  // Use Publisher Journey Query for requests
  const {
    data: publisherJourneyData,
    isLoading: isPublisherJourneyLoading,
    isFetching: isPublisherJourneyFetching,
    total: totalItems,
    refetch,
  } = usePublisherJourneyQuery({
    username: searchUsername,
    status: statusFilter,
    sortOrder: sortOrder,
    sortBy: 'createdAt',
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
  });

  // Refetch data when location changes (navigation)
  useEffect(() => {
    refetch();
  }, [location.key, refetch]);

  const accessToken = Cookies.get('admin_portal_access_token') ?? '';
  const refreshToken = Cookies.get('admin_portal_refresh_token') ?? '';
  
  useEffect(() => {
    if (accessToken || refreshToken) {
      setLocalStorage('accessToken', accessToken);
      setLocalStorage('refreshToken', refreshToken);
    }
  }, [accessToken, refreshToken]);

  // Handler untuk filter dan pencarian
  const handleSearchUsernameChange = (username: string) => {
    setSearchUsername(username);
    const newParams = new URLSearchParams(searchParam);
    
    if (username.trim()) {
      newParams.set('findUsers', username);
    } else {
      newParams.delete('findUsers');
    }
    
    newParams.set('page', '1'); // Reset ke halaman pertama
    setCurrentPage(1);
    setSearchParam(newParams);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    const newParams = new URLSearchParams(searchParam);
    
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }
    
    newParams.set('page', '1'); // Reset ke halaman pertama
    setCurrentPage(1);
    setSearchParam(newParams);
  };

  const handleSortOrderChange = (order: 'ASC' | 'DESC') => {
    setSortOrder(order);
    const newParams = new URLSearchParams(searchParam);
    newParams.set('sortOrder', order);
    setSearchParam(newParams);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    searchParam.set('page', `${pageNumber}`);
    setSearchParam(searchParam);
  };

  const handleDataSizeOption = (dataSizeOption: number) => {
    const pageParam = searchParam.get('page');
    const page = Number(pageParam);
  
    if (page !== null && !isNaN(page) && page > 1) {
      setCurrentPage(1);
      searchParam.set('page', `${1}`);
      setPageSize(dataSizeOption);
      searchParam.set('size', `${dataSizeOption}`);
      setSearchParam(searchParam);
    } else {
      setPageSize(dataSizeOption);
      searchParam.set('size', `${dataSizeOption}`);
      setSearchParam(searchParam);
    }
  };

  // Handler untuk view detail request
  const handleViewDetail = (request: any) => {
    // Navigate to detail page
    navigate(`/requests/detail/${request.id}`, { state: { request } });
  };
  

  return (
    <article>
      <Container>
        <Box as="main">
          {/* Header */} 
          <Box as="header" mb="1rem">
            <h1 className="text-2xl font-bold text-gray-900">Publisher Journey Requests</h1>
            <p className="mt-2 text-gray-600">Manage and review publisher journey requests</p>
          </Box>
          {/* Table with Filters */}
          <RequestsTable
            requests={publisherJourneyData}
            isLoading={isPublisherJourneyLoading || isPublisherJourneyFetching}
            onViewDetail={handleViewDetail}
            searchUsername={searchUsername}
            onSearchUsernameChange={handleSearchUsernameChange}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            sortOrder={sortOrder}
            onSortOrderChange={handleSortOrderChange}
            currentPage={currentPage}
            itemsPerPage={pageSize}
          />
          {/* Pagination */}
          <Box mt="1rem">
            <Flex alignX="flex-end">
              <Pagination
                key={`pagination-${currentPage}-${pageSize}`}
                currentPage={currentPage}
                totalPages={totalItems || 0}
                onClickPage={handlePageChange}
                variant="rounded"
                onClickOption={handleDataSizeOption}
                options={[
                  pageSize.toString(),
                  ...['10', '20', '30', '40', '50'].filter(opt => opt !== pageSize.toString())
                ]}
              />
            </Flex>
          </Box>
        </Box>
      </Container>
    </article>
  );
};

export default RequestPage;